import { ClaimMessage } from '@/api/claims'
import { Collapsible, Input, Typography } from '@/components'
import { ThemeColor } from '@/enums'
import { useSnackbar } from '@/providers'
import { useClaimMessages, useUploadClaimFiles } from '@/services'
import { useDebouncedCallback } from '@/utils'
import { Button } from '@surein/ui'
import { Plus } from 'lucide-react'
import { Fragment, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import ComposeForm, { Attachment } from './ComposeForm'
import MessageItem from './MessageItem'
import {
  ComposeToggleRow,
  LoadMoreRow,
  MessageList,
  MessagesContainer,
} from './styles'

type MessagesTabProps = {
  companyId: string
  claimId: string
}

const MAX_ATTACHMENTS = 10

export const MessagesTab = ({ companyId, claimId }: MessagesTabProps) => {
  const { t } = useTranslation()
  const snackbar = useSnackbar()
  const [search, setSearch] = useState('')
  const debouncedSetSearch = useDebouncedCallback(setSearch, 200)
  const [isComposeOpen, setIsComposeOpen] = useState(false)
  const [subject, setSubject] = useState('')
  const [body, setBody] = useState('')
  const [attachments, setAttachments] = useState<Attachment[]>([])
  const [replyTo, setReplyTo] = useState<ClaimMessage | null>(null)
  const [openItemIds, setOpenItemIds] = useState<Set<string>>(new Set())

  const {
    messages,
    isLoading,
    hasMore,
    isLoadingMore,
    loadMore,
    sendMessage,
    isSending,
    markRead,
    downloadAttachment,
  } = useClaimMessages(companyId, claimId, search)

  const { uploadFiles, isUploading } = useUploadClaimFiles(companyId, claimId)
  const composeSessionRef = useRef(0)

  const resetCompose = () => {
    composeSessionRef.current += 1
    setSubject('')
    setBody('')
    setAttachments([])
    setReplyTo(null)
    setIsComposeOpen(false)
  }

  const handleToggleCompose = () => {
    if (isComposeOpen) {
      resetCompose()
    } else {
      setIsComposeOpen(true)
    }
  }

  const handleReply = (message: ClaimMessage) => {
    setReplyTo(message)
    setSubject(
      `${t('claimsPage.detail.messages.replyPrefix')}${
        message.subject || t('claimsPage.detail.messages.noSubject')
      }`,
    )
    setIsComposeOpen(true)
  }

  const handleFilesPicked = (files: File[]) => {
    if (isUploading) return

    if (attachments.length + files.length > MAX_ATTACHMENTS) {
      snackbar.error(
        t('claimsPage.detail.messages.maxAttachments', {
          max: MAX_ATTACHMENTS,
        }),
      )
      return
    }

    const session = composeSessionRef.current
    uploadFiles(files, {
      onSuccess: (result) => {
        if (composeSessionRef.current !== session) return

        const failedNames = new Set(result.failed)
        const succeededFiles = files.filter(
          (file) => !failedNames.has(file.name),
        )
        const newAttachments = result.succeeded.map((fileId, index) => ({
          file_id: fileId,
          name: succeededFiles[index]?.name ?? fileId,
        }))
        setAttachments((prev) => [...prev, ...newAttachments])
      },
    })
  }

  const handleRemoveAttachment = (fileId: string) => {
    setAttachments((prev) => prev.filter((a) => a.file_id !== fileId))
  }

  const handleSend = () => {
    if (!body.trim() || isUploading) return

    sendMessage(
      {
        subject: subject.trim() || undefined,
        body,
        file_ids: attachments.map((a) => a.file_id),
        reply_to_item_id: replyTo?.item_id,
      },
      {
        onSuccess: resetCompose,
      },
    )
  }

  const handleToggle = (itemId: string, isUnread: boolean) => {
    const opening = !openItemIds.has(itemId)

    setOpenItemIds((prev) => {
      const next = new Set(prev)
      if (opening) {
        next.add(itemId)
      } else {
        next.delete(itemId)
      }
      return next
    })

    if (opening && isUnread) {
      markRead(itemId)
    }
  }

  // A reply opens right above the message it answers; if that message isn't in
  // the current list (filtered out by search, not loaded), fall back to the top
  const isReplyInline =
    isComposeOpen &&
    !!replyTo &&
    messages.some((message) => message.item_id === replyTo.item_id)

  const composeForm = (
    <ComposeForm
      replyTo={replyTo}
      subject={subject}
      body={body}
      attachments={attachments}
      maxAttachments={MAX_ATTACHMENTS}
      isUploading={isUploading}
      isSending={isSending}
      onSubjectChange={setSubject}
      onBodyChange={setBody}
      onFilesPicked={handleFilesPicked}
      onRemoveAttachment={handleRemoveAttachment}
      onCancelReply={resetCompose}
      onSend={handleSend}
    />
  )

  return (
    <MessagesContainer>
      <ComposeToggleRow>
        <Input
          placeholder={t('claimsPage.detail.messages.searchPlaceholder')}
          onChange={(e) => debouncedSetSearch(e.target.value)}
        />
        <Button icon={Plus} variant="outline" onClick={handleToggleCompose}>
          {t('claimsPage.detail.messages.newMessage')}
        </Button>
      </ComposeToggleRow>

      <Collapsible isOpen={isComposeOpen && !isReplyInline}>
        {composeForm}
      </Collapsible>

      {isLoading && (
        <Typography color={ThemeColor.b50} variant="p2Body">
          {t('claimsPage.detail.messages.loading')}
        </Typography>
      )}

      {!isLoading && messages.length === 0 && (
        <Typography color={ThemeColor.b50} variant="p2Body">
          {t('claimsPage.detail.messages.empty')}
        </Typography>
      )}

      {!isLoading && messages.length > 0 && (
        <MessageList>
          {messages.map((message) => (
            <Fragment key={message.item_id}>
              {isReplyInline && replyTo?.item_id === message.item_id && (
                <div>{composeForm}</div>
              )}
              <MessageItem
                message={message}
                isOpen={openItemIds.has(message.item_id)}
                onToggle={() =>
                  handleToggle(message.item_id, message.is_unread)
                }
                onReply={() => handleReply(message)}
                onDownloadAttachment={(attachmentId) =>
                  downloadAttachment({ itemId: message.item_id, attachmentId })
                }
              />
            </Fragment>
          ))}
        </MessageList>
      )}

      {hasMore && (
        <LoadMoreRow>
          <Button variant="outline" onClick={loadMore} disabled={isLoadingMore}>
            {t('claimsPage.detail.messages.loadMore')}
          </Button>
        </LoadMoreRow>
      )}
    </MessagesContainer>
  )
}
